import { graphqlRequest } from "./graphql";

// ── Wallet Balance ──

interface WalletBalanceResponse {
  total: { aggregate: { sum: { amount: number | null } } };
}

export async function getWalletBalance(userId: string, token: string) {
  const query = `
    query GetWalletBalance($user_id: uuid!) {
      total: whatsub_wallet_aggregate(where: {user_id: {_eq: $user_id}}) {
        aggregate {
          sum {
            amount
          }
        }
      }
    }
  `;
  const data = await graphqlRequest<WalletBalanceResponse>(query, { user_id: userId }, token);
  return data.total.aggregate.sum.amount ?? 0;
}

// ── Wallet Analytics (today/yesterday spending & additions) ──

interface AggBucket {
  aggregate: { sum: { amount: number | null } };
}

interface WalletAnalyticsResponse {
  today_spent: AggBucket;
  yesterday_spent: AggBucket;
  today_added: AggBucket;
  yesterday_added: AggBucket;
}

export interface WalletAnalytics {
  todaySpent: number;
  yesterdaySpent: number;
  todayAdded: number;
  yesterdayAdded: number;
}

export async function getWalletAnalytics(
  userId: string,
  todayStart: string,
  todayEnd: string,
  yesterdayStart: string,
  token: string
): Promise<WalletAnalytics> {
  const query = `
    query GetWalletAnalytics(
      $_lte: timestamptz!,
      $_gt: timestamptz!,
      $_gty: timestamptz!,
      $user_id: uuid!
    ) {
      today_spent: whatsub_wallet_aggregate(
        where: {
          type: {_in: ["b2b_buy_debit", "buy_debit", "refund_credit"]},
          created_at: {_lte: $_lte, _gt: $_gt},
          user_id: {_eq: $user_id}
        }
      ) {
        aggregate { sum { amount } }
      }
      yesterday_spent: whatsub_wallet_aggregate(
        where: {
          type: {_in: ["b2b_buy_debit", "refund_credit"]},
          created_at: {_lte: $_gt, _gt: $_gty},
          user_id: {_eq: $user_id}
        }
      ) {
        aggregate { sum { amount } }
      }
      today_added: whatsub_wallet_aggregate(
        where: {
          purpose: {_eq: "Added to wallet"},
          created_at: {_lte: $_lte, _gt: $_gt},
          user_id: {_eq: $user_id}
        }
      ) {
        aggregate { sum { amount } }
      }
      yesterday_added: whatsub_wallet_aggregate(
        where: {
          purpose: {_eq: "Added to wallet"},
          created_at: {_lte: $_gt, _gt: $_gty},
          user_id: {_eq: $user_id}
        }
      ) {
        aggregate { sum { amount } }
      }
    }
  `;
  const data = await graphqlRequest<WalletAnalyticsResponse>(
    query,
    { _lte: todayEnd, _gt: todayStart, _gty: yesterdayStart, user_id: userId },
    token
  );
  return {
    todaySpent: data.today_spent.aggregate.sum.amount ?? 0,
    yesterdaySpent: data.yesterday_spent.aggregate.sum.amount ?? 0,
    todayAdded: data.today_added.aggregate.sum.amount ?? 0,
    yesterdayAdded: data.yesterday_added.aggregate.sum.amount ?? 0,
  };
}

// ── Transactions (paginated + filterable) ──

export interface WalletTransactionRow {
  id: string;
  amount: number;
  created_at: string;
  updated_at: string;
  type: string;
  purpose: string;
  metadata: Record<string, unknown> | null;
}

interface GetTransactionsResponse {
  whatsub_wallet: WalletTransactionRow[];
  whatsub_wallet_aggregate: { aggregate: { count: number } };
}

export interface TransactionFilters {
  userId: string;
  types?: string[];
  createdFrom?: string;
  createdTo?: string;
  search?: string;
}

function buildWhere(filters: TransactionFilters): Record<string, unknown> {
  const where: Record<string, unknown> = {
    user_id: { _eq: filters.userId },
  };
  if (filters.types && filters.types.length > 0) {
    where["type"] = { _in: filters.types };
  }
  const createdAt: Record<string, string> = {};
  if (filters.createdFrom) createdAt["_gte"] = filters.createdFrom;
  if (filters.createdTo) createdAt["_lte"] = filters.createdTo;
  if (Object.keys(createdAt).length > 0) {
    where["created_at"] = createdAt;
  }
  if (filters.search) {
    where["purpose"] = { _ilike: `%${filters.search}%` };
  }
  return where;
}

export async function getTransactions(
  filters: TransactionFilters,
  limit: number,
  offset: number,
  token: string
) {
  const query = `
    query GetTransactions(
      $limit: Int!,
      $offset: Int!,
      $where: whatsub_wallet_bool_exp!
    ) {
      whatsub_wallet(
        limit: $limit,
        offset: $offset,
        order_by: { created_at: desc },
        where: $where
      ) {
        id
        amount
        created_at
        updated_at
        type
        purpose
        metadata
      }
      whatsub_wallet_aggregate(where: $where) {
        aggregate {
          count
        }
      }
    }
  `;
  const where = buildWhere(filters);
  const data = await graphqlRequest<GetTransactionsResponse>(
    query,
    { limit, offset, where },
    token
  );
  return {
    rows: data.whatsub_wallet,
    total: data.whatsub_wallet_aggregate.aggregate.count,
  };
}

// ── All Transactions (for export) ──

interface GetAllTransactionsResponse {
  whatsub_wallet: WalletTransactionRow[];
}

export async function getAllTransactions(
  filters: TransactionFilters,
  token: string
) {
  const query = `
    query GetAllTransactions($where: whatsub_wallet_bool_exp!) {
      whatsub_wallet(
        where: $where
        order_by: { created_at: desc }
      ) {
        id
        amount
        created_at
        updated_at
        type
        purpose
        metadata
      }
    }
  `;
  const where = buildWhere(filters);
  const data = await graphqlRequest<GetAllTransactionsResponse>(
    query,
    { where },
    token
  );
  return data.whatsub_wallet;
}

// ── Transaction Types ──

interface GetTransactionTypesResponse {
  whatsub_wallet: Array<{ type: string }>;
}

export async function getTransactionTypes(userId: string, token: string) {
  const query = `
    query GetTransactionTypes($user_id: uuid!) {
      whatsub_wallet(
        where: { user_id: { _eq: $user_id } }
        distinct_on: type
      ) {
        type
      }
    }
  `;
  const data = await graphqlRequest<GetTransactionTypesResponse>(
    query,
    { user_id: userId },
    token
  );
  return data.whatsub_wallet.map((t) => t.type);
}

// ── Daily Purchases Overview (for chart) ──

interface DailyPurchaseTxn {
  amount: number;
  created_at: string;
}

interface GetWalletTransactionsResponse {
  total: DailyPurchaseTxn[];
}

export async function getDailyPurchases(
  userId: string,
  from: string,
  to: string,
  token: string
) {
  const query = `
    query GetWalletTransactions($_lte: timestamptz!, $_gt: timestamptz!, $user_id: uuid!) {
      total: whatsub_wallet(
        where: {
          type: {_in: ["b2b_buy_debit", "buy_debit", "refund_credit"]},
          created_at: {_lte: $_lte, _gt: $_gt},
          user_id: {_eq: $user_id}
        },
        order_by: {created_at: asc}
      ) {
        amount
        created_at
      }
    }
  `;
  const data = await graphqlRequest<GetWalletTransactionsResponse>(
    query,
    { _lte: to, _gt: from, user_id: userId },
    token
  );
  return data.total;
}
